import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import * as Sentry from "@sentry/react";
import { visualizationStore } from "../../stores/VisualizationStore";
import Analytics from "../../lib/Analytics";

// How long a chip waits for its chart to materialize before it presents as
// "stale" (dashed) and offers a click-to-hydrate retry. A chip is normally
// absent only for the brief window between the LLM emitting the <VizChip> in
// prose and the `visualization` SSE event landing the chart in the store; a
// chip that never resolves is usually a hallucinated chart_id (PLEX-4227).
export const STALE_AFTER_MS = 3000;

export const VizChip = observer(({ id, label }) => {
  const has = visualizationStore.charts.has(id);
  const isActive = visualizationStore.activeChartId === id;
  // Absent-chart lifecycle: loading -> stale (after 3s) -> inert (retry failed).
  const [phase, setPhase] = useState("loading");
  const reportedRef = useRef(false);

  // Drive the ladder off presence. Present → reset. Absent → arm the 3s stale
  // timer; cleared on resolve/unmount/id change so it never fires late.
  useEffect(() => {
    if (has) {
      setPhase("loading");
      reportedRef.current = false;
      return undefined;
    }
    setPhase("loading");
    const t = setTimeout(() => setPhase("stale"), STALE_AFTER_MS);
    return () => clearTimeout(t);
  }, [has, id]);

  // Resolved chart — the interactive reference pill. Wrapped in a sticky anchor
  // so the ACTIVE artifact's reference stays anchored while the transcript
  // scrolls instead of drifting away (position: sticky lives in the CSS class).
  if (has) {
    return (
      <span className={`viz-chip-anchor${isActive ? " viz-chip-anchor-active" : ""}`}>
        <button
          type="button"
          className={`viz-chip${isActive ? " viz-chip-active" : ""}`}
          aria-pressed={isActive}
          aria-label={`Show chart: ${label}`}
          onClick={() => {
            try { Analytics.event("viz.chip_clicked", { chart_id: id }); } catch (e) {}
            visualizationStore.revealChart(id);
          }}
        >
          <span aria-hidden="true">📊</span> {label}
          <span aria-hidden="true">↗</span>
        </button>
      </span>
    );
  }

  // Retry exhausted — the chart could not be hydrated (hallucinated/dropped id).
  if (phase === "inert") {
    return (
      <span className="viz-chip-anchor">
        <button
          type="button"
          disabled
          className="viz-chip viz-chip-inert"
          aria-label={`Chart unavailable: ${label || id}`}
          title="This chart is unavailable"
        >
          <span aria-hidden="true">⚠</span> unavailable
        </button>
      </span>
    );
  }

  // Stale — the chart hasn't landed within the grace window. Offer a manual
  // hydrate; on failure fall to inert and tag the hallucinated id in Sentry.
  if (phase === "stale") {
    const onRetry = () => {
      try { Analytics.event("viz.chip_clicked", { chart_id: id, stale: true }); } catch (e) {}
      if (visualizationStore.resolveChart(id)) {
        visualizationStore.revealChart(id);
        return;
      }
      setPhase("inert");
      if (!reportedRef.current) {
        reportedRef.current = true;
        try {
          Sentry.withScope((scope) => {
            scope.setLevel("warning");
            scope.setTag("viz_chart_id", id);
            Sentry.captureMessage(`VizChip references unresolvable chart_id: ${id}`);
          });
        } catch (e) { /* Sentry must never break the chip */ }
      }
    };
    return (
      <span className="viz-chip-anchor">
        <button
          type="button"
          className="viz-chip viz-chip-stale"
          aria-label={`Retry chart: ${label || id}`}
          title="Chart not loaded yet — click to retry"
          onClick={onRetry}
        >
          <span aria-hidden="true">⟳</span> {label || "retry"}
        </button>
      </span>
    );
  }

  // Loading — the brief grace window before the chart is expected to arrive.
  return (
    <span className="viz-chip-anchor">
      <button
        type="button"
        disabled
        className="viz-chip viz-chip-loading"
        aria-label={`Show chart: ${label || "loading"}`}
        title="Chart loading…"
      >
        <span aria-hidden="true">⋯</span> loading
      </button>
    </span>
  );
});
